<!--
  THE BARE LAYOUT (spec 53 section 3.3). What a gated user sees in place of
  the shell: the company's name, their own name, Sign out, and the training
  page. Nothing here polls and nothing here fetches beyond what the training
  page itself asks for, because the server answers 403 TRAINING_REQUIRED to
  every staff route but a short allowlist (Middleware/RequireTraining.js):
  no rail (its badges count things), no bells (they poll), no live map (it
  streams).

  root.js picks this or app.vue. The terms gate stays (an admin accepts new
  terms here too, and /api/terms is on the allowlist), and so does the one
  toast region, so a failed request still says so.
-->
<template>
  <div class="lw-p-training-shell">
    <header class="lw-p-training-shell__bar">
      <p class="lw-p-training-shell__company" data-test="training-company">
        {{ companyName }}
      </p>
      <div class="lw-p-training-shell__who">
        <span class="lw-p-training-shell__name" data-test="training-user">{{ user.name }}</span>
        <button
          type="button"
          class="lw-p-training-shell__signout"
          data-test="training-signout"
          :disabled="signingOut"
          @click="signOut"
        >
          {{ signingOut ? 'Signing out' : 'Sign out' }}
        </button>
      </div>
    </header>
    <p v-if="signOutFailed" class="lw-p-training-shell__error" role="alert">
      Could not sign out. Check your connection and try again.
    </p>
    <main class="lw-p-training-shell__main">
      <router-view />
    </main>
    <lw-toast-region />
    <terms-gate :user="user" />
  </div>
</template>

<script>
import lwToastRegion from './lw/LwToastRegion.vue';
import termsGate from './TermsGate.vue';
import api from '../../config/api.js';

export default {
  name: 'TrainingLayout',
  components: { lwToastRegion, termsGate },
  props: {
    user: {
      required: true,
      type: Object,
    },
    // Where to go after signing out (and on a 401). Injected so a test does
    // not navigate jsdom, the same seam navSignOut.vue has.
    leave: {
      type: Function,
      default: url => window.location.assign(url),
    },
  },
  data() {
    return { signingOut: false, signOutFailed: false };
  },
  computed: {
    // The organization arrives after this mounts (main.js does not wait for
    // it); "Ladeway" until then, and if it never does.
    companyName() {
      const org = this.$store && this.$store.state && this.$store.state.organization;
      return (org && org.name) || 'Ladeway';
    },
  },
  beforeCreate() {
    // The shell's 401 rule, and only that: a session that ended goes back to
    // sign in. app.vue's other branches (the offline toast, the 402 redirect
    // to Subscription) are for pages this layout never shows.
    this.$http.interceptors.response.use(undefined, err => {
      if (err.response && err.response.status === 401) window.location = api.login;
      throw err;
    });
  },
  methods: {
    async signOut() {
      if (this.signingOut) return;
      this.signingOut = true;
      this.signOutFailed = false;
      try {
        await this.$http.post('/auth/logout');
      } catch (err) {
        this.signingOut = false;
        this.signOutFailed = true;
        return;
      }
      try {
        window.sessionStorage.removeItem('csrf_token');
      } catch (err) {
        // Storage can be unavailable (private mode); the cookie is what mattered.
      }
      this.leave(api.login);
    },
  },
};
</script>

<style scoped>
.lw-p-training-shell {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--lw-bg-canvas);
}

/* The shell's top bar colour, so the gate reads as the same product. */
.lw-p-training-shell__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--lw-space-4);
  min-height: 3rem;
  padding: 0 var(--lw-space-5);
  background: var(--lw-bg-chassis);
  color: var(--lw-fg-on-chassis);
}

.lw-p-training-shell__company {
  margin: 0;
  font: var(--lw-type-subhead);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lw-p-training-shell__who {
  display: flex;
  align-items: center;
  gap: var(--lw-space-4);
  min-width: 0;
}

.lw-p-training-shell__name {
  font: var(--lw-type-body-sm);
  color: var(--lw-fg-on-chassis-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lw-p-training-shell__signout {
  font: var(--lw-type-caption-strong);
  color: var(--lw-fg-on-chassis);
  background: transparent;
  border: 1px solid var(--lw-line-chassis);
  border-radius: var(--lw-radius-sm);
  padding: var(--lw-space-1) var(--lw-space-3);
  cursor: pointer;
  white-space: nowrap;
}

.lw-p-training-shell__signout:hover:not(:disabled) {
  border-color: var(--lw-fg-on-chassis-muted);
}

.lw-p-training-shell__signout:focus-visible {
  outline: var(--lw-focus-ring-width) solid var(--lw-focus-ring-color);
  outline-offset: var(--lw-focus-ring-offset);
}

.lw-p-training-shell__error {
  margin: 0;
  padding: var(--lw-space-2) var(--lw-space-5);
  font: var(--lw-type-body-sm);
  color: var(--lw-danger-fg);
  background: var(--lw-danger-bg);
}

.lw-p-training-shell__main {
  flex: 1;
  overflow-y: auto;
}
</style>
